import * as React from "react";

import { Row, Col } from "antd";
import { takeWhile, range } from "lodash-es";

import { assertNever } from "../../../utils/obj";

import * as theme from "./theme.css";

type GridDirection = "TopToBottom" | "LeftToRight";

/**
 * Props for the GridDisplay component
 * @field columns The number of columns of this grid, cf. GridDisplay spec
 * @field items The array of objects to render using renderItem
 * @field renderItem A function mapping an object to render to a ReactNode. Will get wrapped in a grid before rendering.
 * @field direction The main direction of the grid, either "TopToBottom" or "LeftToRight". Defaults to "TopToBottom".
 */
interface GridProps<T> {
    columns: number;
    items: Array<T>;
    renderItem: (obj: T) => React.ReactNode;
    direction?: GridDirection;
}

/**
 * "Code reuse" component that displays objects in order from top-to-bottom, left-to-right using antd's 24-grid system.
 * Alternatively, it can display objects from left-to-right, top-to-bottom using direction="LeftToRight".
 *
 * With 3 columns, the first 3rd of the options (rounded up) will be inserted in the leftmost column,
 * the second 3rd in the middle column, and the remaining in the rightmost column. The default number of columns is 1.
 * The widget asserts that the number of columns is >= 1."
 *
 * Note: If we want this component to be a React.PureComponent, rendering of this component should be re-triggered by changing
 * a prop field in GridProps. I suggest adding a "value: unknown" field in GridProps if we want that. I'm not sure about the
 * performance impact.
 */
class GridDisplay<T> extends React.Component<GridProps<T>> {
    render = () => {
        if (this.props.items.length === 0) {
            return <div className={theme.imperoGrid} />;
        }
        const nbColumns = this.props.columns >= 1 ? this.props.columns : 1;
        const nbRows = Math.ceil(this.props.items.length / nbColumns);
        const columnsIdx = range(nbColumns);
        const rowsIdx = range(nbRows);

        const direction = this.props.direction ?? "TopToBottom";
        let index: (col: number, row: number) => number;
        switch (direction) {
            case "TopToBottom":
                index = (col, row) => col * nbRows + row;
                break;
            case "LeftToRight":
                index = (col, row) => row * nbColumns + col;
                break;
            default:
                assertNever(direction);
        }

        return (
            <div className={theme.imperoGrid}>
                {rowsIdx.map((row) => (
                    <Row key={"row-" + row}>
                        {takeWhile(
                            columnsIdx,
                            (col) =>
                                this.props.items[index(col, row)] !== undefined
                        ).map((col) => (
                            <Col
                                key={"col-" + col}
                                span={Math.floor(24 / nbColumns)}
                            >
                                {this.props.renderItem(
                                    this.props.items[index(col, row)]
                                )}
                            </Col>
                        ))}
                    </Row>
                ))}
            </div>
        );
    };
}

export { type GridDirection, type GridProps, GridDisplay };
